/**
 * Product Form Component
 * Handles product form submission and cart updates
 */
if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.cart = document.querySelector("cart-notification");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector(
          "[data-js-product-add-to-cart-text]"
        );

        this.hideErrors = this.dataset.hideErrors === "true";

        if (!this.form) {
          console.warn("initializeForm: Product form not found");
          return;
        }

        this.initializeForm();
      }

      /**
       * Initialize form event listeners
       */
      initializeForm() {
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));

        if (document.querySelector("cart-flyover")) {
          this.submitButton?.setAttribute("aria-haspopup", "dialog");
        }
      }

      /**
       * Handle form submission
       */
      onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.submitButton?.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();
        this.setSubmitButtonState(true);

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);

        // Filter out entries with empty values
        const filteredFormData = new FormData();
        for (const [key, value] of formData.entries()) {
          if (value && value.toString().trim() !== "") {
            filteredFormData.append(key, value);
          }
        }

        if (this.cart) {
          filteredFormData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          filteredFormData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = filteredFormData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorResponse(response, formData);
              return;
            }

            this.handleSuccessResponse(response, formData);
          })
          .catch((error) => {
            console.error("Product form submission error:", error);
            this.handleErrorMessage("An error occurred. Please try again.");
          })
          .finally(() => {
            this.setSubmitButtonState(false);
            if (this.cart?.classList.contains("is-empty")) {
              this.cart.classList.remove("is-empty");
            }

            if (typeof CartPerformance !== "undefined") {
              CartPerformance.measureFromEvent("add:user-action", evt);
            }
          });
      }

      /**
       * Handle error responses from cart API
       */
      handleErrorResponse(response, formData) {
        if (
          typeof publish === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined"
        ) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: "product-form",
            productVariantId: formData.get("id"),
            errors: response.errors || response.description,
            message: response.message,
          });
        }

        this.handleErrorMessage(response.description);
        this.handleSoldOutState();
        this.error = true;
      }

      /**
       * Handle successful cart additions
       */
      handleSuccessResponse(response, formData) {
        this.error = false;

        if (this.dataset.navigate === "true") {
          window.location.href = `${routes.cart_url}`;
          return;
        }

        if (
          typeof publish === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined"
        ) {
          const startMarker =
            typeof CartPerformance !== "undefined"
              ? CartPerformance.createStartingMarker("add:wait-for-subscribers")
              : null;

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: formData.get("id"),
            cartData: response,
          }).then(() => {
            if (startMarker && typeof CartPerformance !== "undefined") {
              CartPerformance.measureFromMarker(
                "add:wait-for-subscribers",
                startMarker
              );
            }
          });
        }

        this.renderCartContents(response);
      }

      /**
       * Render cart contents with performance measurement
       */
      renderCartContents(response) {
        if (typeof CartPerformance !== "undefined") {
          CartPerformance.measure("add:paint-updated-sections", () => {
            this.cart?.renderContents(response);
          });
        } else {
          this.cart?.renderContents(response);
        }
      }

      /**
       * Handle sold out state for submit button
       */
      handleSoldOutState() {
        const soldOutMessage =
          this.submitButton?.querySelector(".sold-out-message");
        if (!soldOutMessage) return;

        this.submitButton.setAttribute("aria-disabled", "true");
        this.submitButtonText?.classList.add("hidden");
        soldOutMessage.classList.remove("hidden");
      }

      /**
       * Set submit button loading state
       */
      setSubmitButtonState(isLoading) {
        if (!this.submitButton) return;

        if (isLoading) {
          this.submitButton.setAttribute("aria-disabled", "true");
          this.submitButton.setAttribute("aria-busy", "true");
        } else {
          this.submitButton.removeAttribute("aria-disabled");
          this.submitButton.removeAttribute("aria-busy");
          if (!this.error) {
            this.submitButton.removeAttribute("aria-disabled");
          }
        }
      }

      /**
       * Handle error message display
       */
      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        if (errorMessage) {
          this.errorMessageWrapper.classList.remove("opacity-0");
          this.errorMessageWrapper.classList.remove("hidden");
          this.errorMessageWrapper.classList.add("opacity-100");
          this.errorMessage.textContent = errorMessage;
        } else {
          this.errorMessageWrapper.classList.remove("opacity-100");
          this.errorMessageWrapper.classList.add("opacity-0");
          this.errorMessageWrapper.classList.add("hidden");
        }
      }

      /**
       * Toggle submit button state
       */
      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute("aria-disabled", "true");
          this.submitButton.disabled = true;
          if (text && this.submitButtonText) {
            this.submitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute("aria-disabled");
          this.submitButton.disabled = false;
          if (this.submitButtonText && window.variantStrings?.addToCart) {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      /**
       * Get variant ID input element
       */
      get variantIdInput() {
        // items[0] is for the primary product in a multi-add form like keyboard
        const input =
          this.form?.querySelector('[name="id"]') ||
          this.form?.querySelector('[name="items\\[0\\]\\[id\\]"]');
        return input;
      }

      getSelectedVariantInputs() {
        const selectedInputs = Array.from(
          document.querySelectorAll('[name^="items["][name$="][id]"]')
        ).filter((input) => {
          return input.form === this.form && input.checked;
        });
        return selectedInputs;
      }

      getEstimatedShipDate() {
        try {
          let estimatedShipDate = null;
          const variantInputs = this.getSelectedVariantInputs();

          for (const input of variantInputs) {
            const variantId = input.value;
            if (!variantId || variantId.trim() === "" || input.disabled)
              continue;

            const variantEstimatedShipDate =
              this.getVariantEstimatedShipDate(variantId);
            if (variantEstimatedShipDate) {
              estimatedShipDate =
                variantEstimatedShipDate > estimatedShipDate
                  ? variantEstimatedShipDate
                  : estimatedShipDate;
            }
          }

          return this.formatEstimatedShipDate(estimatedShipDate);
        } catch (e) {
          console.error("Error getting estimated ship date:", e);
          return null;
        }
      }

      getVariantEstimatedShipDate(variantId) {
        const variants = this.getAllVariants();
        const variant = variants.find((v) => v.id == variantId);
        return variant ? variant.estimatedShipDate : null;
      }

      getTotalPrice() {
        try {
          let totalPrice = 0;
          const variantInputs = this.getSelectedVariantInputs();
          for (const input of variantInputs) {
            const variantId = input.value;
            if (!variantId || variantId.trim() === "" || input.disabled)
              continue;

            const price = this.getVariantPrice(variantId);
            if (price) {
              totalPrice += price;
            }
          }

          return this.formatPrice(totalPrice);
        } catch (e) {
          console.error("Error getting total price:", e);
          return 0;
        }
      }

      /**
       * Get price for a specific variant ID from various data sources
       */
      getVariantPrice(variantId) {
        const variants = this.getAllVariants();
        const variant = variants.find((v) => v.id == variantId);

        return variant ? variant.price : 0;
      }

      /**
       * Get selected variant data from script tag
       */
      getSelectedVariant() {
        try {
          const script = document.querySelector(
            "script[data-selected-variant]"
          );
          if (script && script.textContent.trim()) {
            return JSON.parse(script.textContent.trim());
          }
        } catch (e) {
          console.warn("Error parsing selected variant:", e);
        }
        return null;
      }

      getVariants(identifier) {
        try {
          const script = document.querySelector(
            `#${identifier}VariantsData[data-${identifier}-variants]`
          );
          if (script && script.textContent.trim()) {
            return JSON.parse(script.textContent.trim());
          }
        } catch (e) {
          console.warn("Error parsing variants:", e);
        }
        return null;
      }

      getAllVariants() {
        return [
          ...(this.getVariants("keyboard") || []),
          ...(this.getVariants("keycap") || []),
          ...(this.getVariants("switch") || []),
          ...(this.getVariants("stabilizer") || []),
        ];
      }

      /**
       * Format price with the shop's money format (formatMoney in global.js)
       */
      formatPrice(priceInCents) {
        return formatMoney(priceInCents);
      }

      formatEstimatedShipDate(estimatedShipDate) {
        return estimatedShipDate
          ? new Date(estimatedShipDate * 1000).toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            })
          : null;
      }
    }
  );
}
